import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { resetDirectThreadCache } from '@/components/Messenger/cache/directThreadCacheStore';
import { API_BASE_URL } from '@/config/serverConfig';
import { disconnectSharedSocket, reconnectSharedSocket } from '@/hooks/useRealtimeSync';
import {
  requestServerLogout,
  handleForcedSessionEnd,
  refreshAccessSessionWhenReachable,
  retryWhileUnavailable,
  tryRefreshAccessSession,
} from '@/lib/authSession';
import { unsubscribeWebPushFromServer } from '@/notifications/webPushSubscribe';
import {
  parseAuthSessionBody,
  type AuthSessionResponseBody,
} from '@/types/access';
import {
  AuthContext,
  EMPTY_CAPABILITIES,
  type AuthSessionPayload,
  type AuthUser,
} from './authStateContext';

export type { AuthUser } from './authStateContext';

/** Запас до exp access JWT: proactive refresh. */
const SESSION_REFRESH_MARGIN_MS = 60_000;
const SESSION_END_MARGIN_MS = 3000;
const VERIFY_TIMEOUT_MS = 15_000;

function parseSessionExpiryMs(iso: string | undefined): number | null {
  if (!iso || typeof iso !== 'string') return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/** Ответ /auth/verify: 'none' — входа нет; 'unavailable' — сервер не ответил, о сессии ничего не известно. */
type VerifyResult =
  | { kind: 'session'; session: AuthSessionPayload }
  | { kind: 'none' }
  | { kind: 'deactivated' }
  | { kind: 'unavailable' };

const isVerifyUnavailable = (r: VerifyResult): r is { kind: 'unavailable' } => r.kind === 'unavailable';

async function fetchVerifiedSession(): Promise<VerifyResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/auth/verify`, {
      credentials: 'include',
      signal: AbortSignal.timeout(VERIFY_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unavailable' };
  }
  if (res.status === 204) return { kind: 'none' };
  const data = (await res.json().catch(() => null)) as AuthSessionResponseBody | null;
  if (await handleForcedSessionEnd(res.status, data)) return { kind: 'deactivated' };
  const session = res.ok && data ? parseAuthSessionBody(data) : null;
  if (session) return { kind: 'session', session };
  // 2xx без сессии в теле — страница прокси или captive portal, 5xx — сервер перезапускается.
  return res.ok || res.status >= 500 ? { kind: 'unavailable' } : { kind: 'none' };
}

/** Сессия при загрузке: verify; без access-cookie — refresh и снова verify. */
async function loadSession(): Promise<VerifyResult> {
  const first = await fetchVerifiedSession();
  if (first.kind !== 'none') return first;
  const outcome = await tryRefreshAccessSession();
  if (outcome === 'rejected') return { kind: 'none' };
  if (outcome === 'unavailable') return { kind: 'unavailable' };
  return fetchVerifiedSession();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [capabilities, setCapabilities] = useState(EMPTY_CAPABILITIES);
  const [preview, setPreview] = useState<AuthSessionPayload['preview']>(null);
  const [groupSlug, setGroupSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const sessionExpiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Поколение таймера: обновление, начатое до выхода или новой сессии, своего итога не применяет. */
  const expiryGenerationRef = useRef(0);
  const logoutRef = useRef<() => Promise<void>>(async () => {});
  const setSessionRef = useRef<(s: AuthSessionPayload) => void>(() => {});
  const sessionRef = useRef<AuthSessionPayload | null>(null);

  const clearClientSession = useCallback(() => {
    sessionRef.current = null;
    setUser(null);
    setCapabilities(EMPTY_CAPABILITIES);
    setPreview(null);
    setGroupSlug(null);
  }, []);

  const clearSessionExpiryTimer = useCallback(() => {
    expiryGenerationRef.current += 1;
    if (sessionExpiryTimerRef.current != null) {
      clearTimeout(sessionExpiryTimerRef.current);
      sessionExpiryTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    clearSessionExpiryTimer();
    resetDirectThreadCache();
    disconnectSharedSocket();
    await unsubscribeWebPushFromServer();
    await requestServerLogout();
    clearClientSession();
  }, [clearClientSession, clearSessionExpiryTimer]);

  logoutRef.current = logout;

  const applySession = useCallback((session: AuthSessionPayload) => {
    sessionRef.current = session;
    setUser(session.user);
    setCapabilities(session.capabilities);
    setPreview(session.preview);
    setGroupSlug(session.group_slug);
  }, []);

  const scheduleSessionExpiry = useCallback(
    (sessionExpiresAt: string | undefined) => {
      clearSessionExpiryTimer();
      const endMs = parseSessionExpiryMs(sessionExpiresAt);
      if (endMs == null) return;
      const refreshDelay = endMs - Date.now() - SESSION_REFRESH_MARGIN_MS;
      const hardDelay = endMs - Date.now() - SESSION_END_MARGIN_MS;

      const generation = ++expiryGenerationRef.current;
      // Сервер недоступен — ждём связи, не выходим: выход только когда сервер сам отказал в сессии.
      const runRefresh = async () => {
        const outcome = await refreshAccessSessionWhenReachable();
        if (generation !== expiryGenerationRef.current) return;
        if (outcome === 'rejected') {
          await logoutRef.current();
          return;
        }
        const verified = await retryWhileUnavailable(fetchVerifiedSession, isVerifyUnavailable);
        if (generation !== expiryGenerationRef.current) return;
        if (verified.kind === 'session') {
          setSessionRef.current(verified.session);
          reconnectSharedSocket();
          return;
        }
        if (verified.kind === 'none') await logoutRef.current();
      };

      if (hardDelay <= 0) {
        void runRefresh();
        return;
      }
      const delay = refreshDelay > 0 ? refreshDelay : Math.max(0, hardDelay);
      sessionExpiryTimerRef.current = setTimeout(() => {
        sessionExpiryTimerRef.current = null;
        void runRefresh();
      }, delay);
    },
    [clearSessionExpiryTimer]
  );

  const setSession = useCallback(
    (session: AuthSessionPayload) => {
      applySession(session);
      scheduleSessionExpiry(session.sessionExpiresAt);
    },
    [applySession, scheduleSessionExpiry]
  );

  setSessionRef.current = setSession;

  const getSession = useCallback(() => sessionRef.current, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Сервер недоступен при загрузке — ждём его, а не показываем вход: сессия, скорее всего, жива.
      const result = await retryWhileUnavailable(loadSession, isVerifyUnavailable);
      if (cancelled) return;
      if (result.kind === 'session') setSession(result.session);
      else clearClientSession();
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [clearClientSession, setSession]);

  useEffect(
    () => () => {
      clearSessionExpiryTimer();
    },
    [clearSessionExpiryTimer]
  );

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    capabilities,
    preview,
    group_slug: groupSlug,
    setSession,
    getSession,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
